/**
 * The events that answer whether anyone comes back — and what the coach's offers
 * are worth.
 *
 * Deliberately few, and deliberately anonymous. There is no account system in
 * this app and this is not the start of one: the id here is a random value in
 * this browser, it is never sent to a provider, and it carries nothing about what
 * was explored. Names of claims, node text, keys and corpus content never reach
 * this file.
 *
 * Both questions are written down in `docs/metrics.md`, with a reason per event,
 * and every event has to be registered there as a dashboard goal — an
 * unregistered custom event is dropped silently and reads as a zero.
 */

/**
 * Set only in the deploy that should report. Unset in every clone, which is why
 * the events are sent from here rather than from anything in `index.html`: a
 * self-hoster must not ship traffic to someone else's dashboard from a page
 * holding their API key.
 *
 * Read at MODULE SCOPE on purpose. Vite replaces `import.meta.env.VITE_*` at
 * build time, so in an unconfigured build this is the literal `undefined` and
 * every guard below folds to a constant — which is what lets the bundler drop the
 * whole body, the endpoint URL included. Reading it inside the functions instead
 * would leave that URL sitting in `dist/` on every clone, and
 * `scripts/analytics-dist.mjs` greps both builds for exactly that.
 */
const DOMAIN = import.meta.env.VITE_ANALYTICS_DOMAIN as string | undefined

/**
 * Plausible's Events API, called by this file and by nothing the vendor serves
 * (D343). Until D343 the deploy injected `plausible.io/js/script.tagged-events.js`:
 * an unversioned file with no integrity pin, executing with full access to a page
 * whose `localStorage` holds every provider key, and listed in an ENFORCED
 * `script-src` so the policy admitted it too. All the app ever needed from it was
 * one POST per event. That POST is now built here, where it can be read, and
 * `script-src` is `'self'` plus one hash again.
 */
const ENDPOINT = 'https://plausible.io/api/event'

const ID_KEY = 'nv.device'
const LAST_SEEN = 'nv.lastSeen'
const DAY_MS = 24 * 60 * 60 * 1000

/**
 * The ten, and no eleventh without a reason written in `docs/metrics.md`.
 *
 * A closed union rather than a string, so a typo becomes a compile error instead
 * of an event that is silently dropped by the dashboard and reads as a zero.
 *
 * The three `cue_*` members answer one question about the guidance layer and
 * nothing else: of what the coach offers, what gets taken, what gets waved away,
 * and what gets silenced outright. They carry `kind` — itself a closed union of
 * seventeen cue names — and never a title, a body or an anchor id, so what was
 * explored still never reaches this file.
 *
 * The four epistemic members answer the question the retention gate cannot:
 * whether the app produces GROUNDED outcomes at all. Each carries one word from
 * a closed union the app already keeps — a source reading, an outcome bearing,
 * a ladder rung, an audit tally — and nothing about which claim, which pair or
 * whose exploration. A dashboard where `check_landed` is mostly `nothing-found`
 * and `bridge_rung` never passes `searched` describes a tool that searches and
 * proposes and settles nothing, which is the one failure this file exists to
 * make visible.
 */
type Event =
  | 'key_saved'
  | 'exploration_completed'
  | 'session_resumed'
  | 'cue_acted'
  | 'cue_skipped'
  | 'cue_muted'
  | 'check_landed'
  | 'outcome_recorded'
  | 'bridge_rung'
  | 'audit_verdict'

/**
 * A random value in this browser, minted on first use.
 *
 * Called only from inside the `DOMAIN` and `reporting()` guards, never before them. An unconfigured
 * clone must not write this: an identifier sitting in the storage of somebody who
 * opted into nothing is harmless in effect and indefensible in a review.
 */
function deviceId(): string {
  try {
    let id = localStorage.getItem(ID_KEY)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(ID_KEY, id)
    }
    return id
  } catch {
    // Private mode, or storage disabled. An unmeasured session is better than a
    // thrown one — and better than a dropped event, so the visit still counts,
    // just without an identity attached to it.
    return 'unavailable'
  }
}

/**
 * The hosts the vendor script refused to report from, plus `*.localhost`, which
 * it did not refuse and which is a developer's machine just the same. `[::1]` and
 * `[::]` are how `location.hostname` spells the IPv6 loopback.
 */
const LOCAL_HOST = /^localhost$|\.localhost$|^127(\.[0-9]+){0,2}\.[0-9]+$|^\[::1?\]$/

/**
 * Whether this page may report at all, given a configured domain.
 *
 * The vendor script made these exclusions for us, and dropping it must not drop
 * them (D343): a developer's own machine, a `file:` copy, a browser driven by
 * automation — the e2e suite runs with `navigator.webdriver` true, and a test run
 * counted as a visitor is a fabricated number — and the vendor's own opt-out,
 * `localStorage.plausible_ignore = 'true'`, which the owner has already set in
 * the browsers they use and which must keep working without being set again.
 *
 * Read on every call rather than once: the opt-out is toggled in a live console,
 * and that should take effect without a reload, as it did with the script.
 */
function reporting(): boolean {
  if (typeof location === 'undefined' || typeof navigator === 'undefined') return false
  if (location.protocol === 'file:' || LOCAL_HOST.test(location.hostname)) return false
  const g = globalThis as { _phantom?: unknown; __nightmare?: unknown; Cypress?: unknown }
  if (navigator.webdriver || g._phantom || g.__nightmare || g.Cypress) return false
  try {
    if (localStorage.getItem('plausible_ignore') === 'true') return false
  } catch {
    // Storage that cannot be read cannot hold an opt-out either, so this browser
    // never set one. Reporting is the reading that matches what was asked.
  }
  return true
}

/**
 * The parameters Plausible reads from `url` for Sources and Campaigns, per its
 * docs: the five UTM tags, `ref` and `source`. Nothing else in a query is kept.
 * `gclid` and `msclkid` are left out though Plausible reads them too: each is an
 * identifier for one ad click, and this app runs no ads to attribute.
 */
const CAMPAIGN = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'ref',
  'source',
]

/**
 * The page, as the dashboard may see it: origin and path, plus campaign tags.
 *
 * Never the fragment. A share link carries the whole exploration there (D332),
 * and although boot clears it before the first pageview, an address read here
 * must not depend on somebody else's ordering to keep a graph out of a vendor's
 * logs. Never an arbitrary query either — the script sent `location.href` whole,
 * which reported whatever a link had appended. This is stricter than it was.
 */
function pageUrl(): string {
  const query = new URLSearchParams(location.search)
  const kept = new URLSearchParams()
  for (const k of CAMPAIGN) {
    const v = query.get(k)
    if (v !== null) kept.set(k, v)
  }
  const q = kept.toString()
  return `${location.origin}${location.pathname}${q ? `?${q}` : ''}`
}

/**
 * Where the visitor came from, as an origin and nothing more.
 *
 * Plausible derives a Source from the referrer's host, and says itself that it
 * shows only the domain. The script sent `document.referrer` whole, which is
 * whatever the referring page's own policy let through — a full URL, path and
 * query, wherever that page allows it. The origin is all the dashboard uses, so
 * it is all that leaves (D343). The cost is the drill-down into which page on a
 * site linked here. A referrer from this same origin is not a source, and an
 * unparseable one says nothing worth sending.
 *
 * `android-app:` is the third scheme Plausible's ingestion accepts, and it keeps
 * the prefix, so a tap in the Reddit or Slack app on Android reads as that app's
 * Source rather than Direct. Its "host" is the app's package name, which is all
 * that is sent. Built from protocol and host because such a URL's `origin` is
 * the string `'null'`.
 */
function referrer(): string | null {
  try {
    const raw = typeof document === 'undefined' ? '' : document.referrer
    if (!raw) return null
    const from = new URL(raw)
    if (!from.host) return null
    if (from.protocol === 'android-app:') return `android-app://${from.host}`
    if (from.protocol !== 'https:' && from.protocol !== 'http:') return null
    if (from.origin === location.origin) return null
    return `${from.origin}/`
  } catch {
    return null
  }
}

/**
 * One event, one POST, and never an exception.
 *
 * The body is JSON sent as `text/plain`, which the Events API accepts and which
 * keeps the request CORS-simple: no preflight, so one request per event rather
 * than two, exactly as the vendor script sent it. The visitor is identified by
 * Plausible from the request's User-Agent and IP with a daily salt, which the
 * browser supplies on its own; nothing here adds to it.
 *
 * `sendBeacon` first, because it survives the page closing — `exploration_completed`
 * can fire as somebody leaves. It returns false when the browser will not queue
 * the request (a full queue, a size limit), and then `fetch` with `keepalive`,
 * the other way a request outlives its page, sends the same body. `credentials:
 * 'omit'` there because nothing Plausible needs is in a cookie.
 */
function send(name: string, props?: Record<string, string>, interactive = true): void {
  try {
    const body = JSON.stringify({
      name,
      url: pageUrl(),
      domain: DOMAIN,
      referrer: referrer(),
      ...(props ? { props } : {}),
      ...(interactive ? {} : { interactive: false }),
    })
    try {
      if (
        typeof navigator.sendBeacon === 'function' &&
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
      ) {
        return
      }
    } catch {
      // A beacon that throws is a beacon that did not queue; the fallback below
      // is the same request by the other road.
    }
    if (typeof fetch !== 'function') return
    void fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain' },
      body,
    }).catch(() => {
      // An ad blocker refusing the request is a normal condition, not an error.
    })
  } catch {
    // Analytics must never break a run.
  }
}

export function track(event: Event, props: Record<string, string> = {}): void {
  if (!DOMAIN) return
  try {
    if (!reporting()) return
    send(event, { ...props, device: deviceId() })
  } catch {
    // Analytics must never break a run. Every path above already catches; this
    // is the one guard that does not depend on each of them remembering to.
  }
}

/**
 * Runs `fn` now if the page is showing, or once when it is first shown.
 *
 * A page loaded hidden — a background tab restored by the browser, a prerender —
 * is not a visit until somebody looks at it, as the script's pageview held too.
 * `!== 'hidden'` rather than `=== 'visible'`: a browser that does not report
 * visibility at all is showing the page, and must still count it.
 */
function whenShown(fn: () => void): void {
  if (document.visibilityState !== 'hidden') return fn()
  const shown = () => {
    if (document.visibilityState === 'hidden') return
    document.removeEventListener('visibilitychange', shown)
    fn()
  }
  document.addEventListener('visibilitychange', shown)
}

/**
 * Called once at boot. Fires `session_resumed` when the gap since the last visit
 * is over a day, which is the numerator of the retention question.
 *
 * Held to the pageview's moment (D343): a tab restored in the background and
 * never opened is no return, and stamping it would shorten the gap the real
 * return reports. Sent non-interactive, because nobody did anything — the event
 * fires at boot — so it must not turn a returning visit that only looked into a
 * non-bounce. Until D343 this event never reached the dashboard at all: it fired
 * in the same turn the vendor script was appended, before `window.plausible`
 * existed and with no queue stub, so its series starts at D343.
 */
export function markSession(): void {
  if (!DOMAIN || typeof document === 'undefined') return
  try {
    whenShown(() => {
      try {
        // An excluded browser keeps no visit stamp either: a developer's localhost
        // or an opted-out browser is not a visit, so it is not the start of a gap.
        if (!reporting()) return
        // `Number(null)` is 0 and `Number('nonsense')` is NaN; both are falsy
        // enough for the guard below, so a corrupt stamp reads as a first visit
        // rather than throwing or announcing a return from an impossible date.
        const prev = Number(localStorage.getItem(LAST_SEEN) ?? 0)
        const now = Date.now()
        if (prev && now - prev > DAY_MS) {
          const days = String(Math.floor((now - prev) / DAY_MS))
          send('session_resumed', { days, device: deviceId() }, false)
        }
        localStorage.setItem(LAST_SEEN, String(now))
      } catch {
        /* see `track` */
      }
    })
  } catch {
    /* see `track` */
  }
}

/** Guards against a second pageview. `App.tsx` bootstraps under StrictMode, which
 *  double-invokes effects, and two pageviews per visit means every visit counted
 *  twice — worse than counting none, because it looks like data. */
let started = false

/**
 * The one pageview, sent at boot (D343). The vendor script sent it on load and
 * again on every `history.pushState`; this app has no router and only ever calls
 * `replaceState` (to clear a share link), which the script never counted, so one
 * per load is the count it produced. Opening a second share link by its fragment
 * is not a page either — the script ran without hash mode.
 *
 * A page loaded hidden — a background tab, a prerender — waits until it is
 * first shown, as the script's pageview did: a page nobody has looked at is not
 * a visit, and counting it would inflate the denominator of the retention gate.
 * No device id rides on the pageview; the script's never carried one.
 */
export function initAnalytics(): void {
  if (!DOMAIN || started || typeof document === 'undefined') return
  started = true
  try {
    whenShown(() => {
      if (reporting()) send('pageview')
    })
  } catch {
    /* see above */
  }
}
